export const MATERIALS = [
  { key: 'hardwood_mulch', name: 'Hardwood Mulch', unit: 'yd' },
  { key: 'topsoil', name: 'Topsoil', unit: 'yd' },
  { key: 'screened_sand', name: 'Screened Sand', unit: 'ton' },
  { key: 'crushed_gravel', name: 'Crushed Gravel', unit: 'ton' },
  { key: 'river_rock', name: 'River Rock', unit: 'ton' },
  { key: 'sod', name: 'Sod', unit: 'yd' },
] as const;

export type MaterialKey = (typeof MATERIALS)[number]['key'];

// Inventory at or below this (native unit) is "low" in the prices grid.
export const INVENTORY_LOW = 25;

export const MATERIAL_NAME: Record<string, string> = Object.fromEntries(
  MATERIALS.map((m) => [m.key, m.name])
);
export const MATERIAL_UNIT: Record<string, string> = Object.fromEntries(
  MATERIALS.map((m) => [m.key, m.unit])
);

export interface Team {
  id: string;
  name: string;
  invite_code: string;
}

export interface TeamSettings {
  team_id: string;
  show_labels: boolean;
  use_clustering: boolean;
  cost_per_mile: number;
  yards_per_load: number;
  tons_per_load: number;
  min_cost_per_mile: number;
  max_cost_per_mile: number;
  freight_curve_coefficients: number[] | null;
  freight_curve_fit_at: string | null;
  freight_curve_n_points: number;
  updated_at: string;
}

export interface Yard {
  team_id: string;
  yard_id: string;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  lat: number | null;
  lon: number | null;
  hardwood_mulch: boolean;
  topsoil: boolean;
  screened_sand: boolean;
  crushed_gravel: boolean;
  river_rock: boolean;
  sod: boolean;
  updated_at: string;
}

export interface Site {
  team_id: string;
  site_id: string;
  name: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  lat: number | null;
  lon: number | null;
  updated_at: string;
}

export interface YardMaterialPrice {
  team_id: string;
  yard_id: string;
  material: MaterialKey;
  price: number | null;
  inventory: number;
  updated_at: string;
}

export interface PricingRow {
  yard_id: string;
  site_id: string;
  material: string;
  material_price: number | null;
  distance_miles: number | null;
  cost_per_mile: number | null;
  freight_cost: number | null;
  delivered_price: number | null;
  site_name?: string | null;
  site_city?: string | null;
  site_state?: string | null;
  site_address?: string | null;
  site_lat?: number | null;
  site_lon?: number | null;
}

export interface SourcingRow {
  yard_id: string;
  yard_name: string | null;
  yard_lat: number | null;
  yard_lon: number | null;
  material_price: number | null;
  distance_miles: number | null;
  freight_cost: number | null;
  delivered_price: number | null;
  inventory: number;
  is_cheapest: boolean;
}

export interface DeliveryRecord {
  id: number;
  delivery_date: string;
  yard_city: string | null;
  yard_state: string | null;
  site_city: string | null;
  site_state: string | null;
  material: string | null;
  haul_fee: number | null;
  quantity: number | null;
}

export type OrderStatus = 'open' | 'in_progress' | 'filled';

export interface Order {
  id: string;
  created_at: string;
  order_no: string | null;
  customer_name: string | null;
  yard_id: string;
  site_id: string | null;
  site_address: string;
  material: string;
  quantity: number;
  delivered_price_per_unit: number | null;
  delivered_total: number | null;
  status: OrderStatus;
}

export const ORDER_STATUS_LABEL: Record<OrderStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  filled: 'Filled',
};

// AI planner action proposals
export type AIAction =
  | {
      type: 'update_price';
      yard_id: string;
      material: MaterialKey;
      new_price: number;
      display: string;
    }
  | {
      type: 'create_order';
      material: MaterialKey;
      quantity: number;
      unit: string;
      site_id: string;
      site_address: string;
      yard_id: string;
      yard_name: string | null;
      material_price: number | null;
      distance_miles: number | null;
      freight_cost: number | null;
      delivered_price_per_unit: number;
      delivered_total: number;
      display: string;
    }
  | { type: 'clarify'; display: string }
  | { type: 'feature_request'; summary: string };

export interface AIResponse {
  reply: string;
  actions: AIAction[];
}
